import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SortHeader, { sortRows } from "@/components/SortHeader";
import ExportButton from "@/components/ExportButton";
import { Bank, CheckCircle, Warning, ArrowClockwise, MagnifyingGlass, CaretDown, CaretRight, X, ArrowLeft, DownloadSimple, FilePdf, FileXls } from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";

const inr = (n) => {
    if (n === null || n === undefined) return "—";
    return "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(n));
};
const inr0 = (n) => (n === null || n === undefined) ? "—" : "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(Number(n)));

// v1.25.73 — Compact download-both button pair for a drill-down row. Uses
// the same axios instance so cookies + JWT headers are attached; falls back
// to a same-tab click if window.open is blocked by the mobile browser.
function DrillDownDownload({ params, testIdBase }) {
    const [busy, setBusy] = useState(null);
    const download = async (kind) => {
        try {
            setBusy(kind);
            const url = kind === "pdf"
                ? "/export/payments-by-upi-details.pdf"
                : "/export/payments-by-upi-details.xlsx";
            const cleanParams = Object.fromEntries(
                Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== "")
            );
            const res = await api.get(url, { params: cleanParams, responseType: "blob" });
            const blob = new Blob([res.data], {
                type: kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const disp = res.headers?.["content-disposition"] || "";
            const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
            const filename = m ? decodeURIComponent(m[1]) : withDatePrefix(`payments-by-upi-drilldown.${kind === "pdf" ? "pdf" : "xlsx"}`);
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };
    return (
        <div className="flex items-center gap-1.5" data-testid={`${testIdBase}-download`}>
            <button
                type="button"
                data-testid={`${testIdBase}-pdf`}
                disabled={!!busy}
                onClick={() => download("pdf")}
                className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-700 shadow-sm transition hover:bg-indigo-50 disabled:opacity-60"
                title="Download this drill-down as PDF"
            >
                <FilePdf size={11} weight="bold" /> {busy === "pdf" ? "…" : "PDF"}
            </button>
            <button
                type="button"
                data-testid={`${testIdBase}-xlsx`}
                disabled={!!busy}
                onClick={() => download("xlsx")}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 shadow-sm transition hover:bg-emerald-50 disabled:opacity-60"
                title="Download this drill-down as Excel"
            >
                <FileXls size={11} weight="bold" /> {busy === "xlsx" ? "…" : "Excel"}
            </button>
        </div>
    );
}

export default function PaymentsByUpi() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // v1.25.35 — deep-link params: ?master=<mapping_id>&upi=<upi_id>&back=<url>
    const initMaster = searchParams.get("master") || "";
    const initUpi = searchParams.get("upi") || "";
    const backTo = searchParams.get("back") || "";
    const [rows, setRows] = useState([]);
    const [unused, setUnused] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [parties, setParties] = useState([]);
    const [partyId, setPartyId] = useState("");
    const [masters, setMasters] = useState([]);
    const [masterId, setMasterId] = useState(initMaster);
    const [groupByMaster, setGroupByMaster] = useState(true);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState({ by: "amount", dir: "desc" });
    const [expanded, setExpanded] = useState(null);
    const [detailsCache, setDetailsCache] = useState({});
    const [detailsLoadingKey, setDetailsLoadingKey] = useState(null);
    const [slipSort, setSlipSort] = useState({ by: null, dir: null });

    useEffect(() => {
        api.get("/parties").then((r) => setParties(Array.isArray(r.data) ? r.data.filter((p) => p.active !== false) : [])).catch(() => {});
        api.get("/upi-mappings").then((r) => setMasters(Array.isArray(r.data?.mappings) ? r.data.mappings : [])).catch(() => {});
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = { group_by_master: groupByMaster };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (partyId) params.party_id = partyId;
            if (masterId) params.master_upi_id = masterId;
            if (initUpi) params.upi_id = initUpi;
            const { data } = await api.get("/reports/payments-by-upi", { params });
            const gotRows = Array.isArray(data.rows) ? data.rows : [];
            setRows(gotRows);
            setUnused(Array.isArray(data.unused_whitelisted) ? data.unused_whitelisted : []);
            setTotalCount(data.total_count || 0);
            setTotalAmount(data.total_amount || 0);
            // v1.25.36 — if we arrived via ?upi=<id> or ?master=<id>, auto-expand
            // the matching row so the drill-down slips are shown immediately.
            // v1.25.37 — same behaviour when the user picks a Master from the
            // dropdown: only 1 row is returned → open it and preload slips so
            // "select master → see slips" is a single interaction.
            if (initUpi && gotRows.length > 0) {
                const match = gotRows.find((r) => (r.upi_id || "").toLowerCase() === initUpi.toLowerCase()) || gotRows[0];
                setExpanded(match.is_master ? `M:${match.mapping_id}` : (match.upi_id || "__unknown__"));
                setTimeout(() => toggleExpand(match), 100);
            } else if (masterId && gotRows.length > 0) {
                const match = gotRows.find((r) => r.mapping_id === masterId) || gotRows[0];
                setExpanded(match.is_master ? `M:${match.mapping_id}` : (match.upi_id || "__unknown__"));
                setTimeout(() => toggleExpand(match), 100);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [masterId, partyId]);

    const toggleExpand = async (r) => {
        const key = r.is_master ? `M:${r.mapping_id}` : (r.upi_id || "__unknown__");
        if (expanded === key) { setExpanded(null); return; }
        setExpanded(key);
        if (detailsCache[key]) return;
        setDetailsLoadingKey(key);
        try {
            const params = {};
            if (r.is_master && r.mapping_id) {
                params.master_upi_id = r.mapping_id;
            } else {
                params.upi_id = r.upi_id || "";
            }
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (partyId) params.party_id = partyId;
            const { data } = await api.get("/reports/payments-by-upi/details", { params });
            setDetailsCache((prev) => ({ ...prev, [key]: data.slips || [] }));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDetailsLoadingKey(null); }
    };

    // Clear the drill-down cache whenever any filter changes so re-expand refetches
    useEffect(() => { setDetailsCache({}); setExpanded(null); }, [dateFrom, dateTo, partyId, masterId, groupByMaster]);

    const openSlip = (s) => navigate(`/admin/uploads?utr=${encodeURIComponent(s.utr || "")}&_open=${Date.now()}`);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        const list = !t ? rows : rows.filter((r) =>
            (r.upi_id || "").toLowerCase().includes(t) ||
            (r.display || "").toLowerCase().includes(t) ||
            (r.whitelist_label || "").toLowerCase().includes(t) ||
            (r.party_names_sample || []).some((n) => n.toLowerCase().includes(t))
        );
        return sortRows(list, sort);
    }, [rows, q, sort]);

    const unknownRow = rows.find((r) => !r.upi_id);
    const whitelistedCount = rows.filter((r) => r.in_whitelist).length;
    const unregisteredCount = rows.filter((r) => r.upi_id && !r.in_whitelist).length;

    return (
        <div data-testid="payments-by-upi-page" className="space-y-6">
            {backTo && (
                <div className="flex items-center gap-2">
                    <Button
                        data-testid="upi-report-back"
                        type="button"
                        onClick={() => navigate(backTo)}
                        variant="outline"
                        className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                        <ArrowLeft size={12} className="mr-1" weight="bold" /> Back to UPI Mappings
                    </Button>
                </div>
            )}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900">Payments received · by UPI</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Verified slip payments grouped by receiver UPI. Filter by party to see "how much did X pay me and on which UPIs". Click any UPI row to drill into the individual slips.
                    </p>
                    {initUpi && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-200">
                            <MagnifyingGlass size={11} weight="bold" /> Pre-filtered to <span className="font-mono">{initUpi}</span>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[220px]">
                        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Master UPI</Label>
                        <Select value={masterId || "__all__"} onValueChange={(v) => setMasterId(v === "__all__" ? "" : v)}>
                            <SelectTrigger data-testid="upi-report-master" className="mt-1 h-9 text-sm">
                                <SelectValue placeholder="All UPIs" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All UPIs</SelectItem>
                                {masters.map((m) => (
                                    <SelectItem key={m.id} value={m.id}>
                                        {m.label ? `${m.label} — ${m.master_upi_id}` : m.master_upi_id}
                                        {` · ${(m.linked_upi_ids || []).length} linked`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="min-w-[220px]">
                        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Party (optional)</Label>
                        <Select value={partyId || "__all__"} onValueChange={(v) => setPartyId(v === "__all__" ? "" : v)}>
                            <SelectTrigger data-testid="upi-report-party" className="mt-1 h-9 text-sm">
                                <SelectValue placeholder="All parties" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All parties</SelectItem>
                                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">From</Label>
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="upi-report-from" className="mt-1 w-40" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">To</Label>
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="upi-report-to" className="mt-1 w-40" />
                    </div>
                    <Button onClick={load} disabled={loading} data-testid="upi-report-apply" className="bg-slate-900 text-white hover:bg-slate-800">
                        <ArrowClockwise size={14} className="mr-1.5" /> {loading ? "Loading…" : "Apply"}
                    </Button>
                    <ExportButton
                        endpoint={{ pdf: "/export/payments-by-upi.pdf", xlsx: "/export/payments-by-upi.xlsx" }}
                        params={{
                            ...(dateFrom ? { date_from: dateFrom } : {}),
                            ...(dateTo ? { date_to: dateTo } : {}),
                            ...(partyId ? { party_id: partyId } : {}),
                            ...(masterId ? { master_upi_id: masterId } : {}),
                            ...(initUpi ? { upi_id: initUpi } : {}),
                        }}
                        filenameBase="payments-by-upi"
                        testId="upi-report-export"
                    />
                    {(partyId || dateFrom || dateTo || masterId) && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => { setPartyId(""); setMasterId(""); setDateFrom(""); setDateTo(""); setTimeout(load, 0); }}
                            data-testid="upi-report-clear"
                            className="border-slate-300 text-slate-700"
                        >
                            <X size={12} className="mr-1" /> Clear
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Total received</div>
                    <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="upi-total-amount">{inr0(totalAmount)}</div>
                    <div className="text-[11px] text-slate-500">{totalCount} slip{totalCount === 1 ? "" : "s"}</div>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700">Whitelisted UPIs</div>
                    <div className="mt-1 font-display text-2xl font-bold text-emerald-800">{whitelistedCount}</div>
                </div>
                <div className={`rounded-lg border ${unregisteredCount > 0 ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white"} p-4 shadow-sm`}>
                    <div className={`text-[11px] uppercase tracking-wider ${unregisteredCount > 0 ? "text-amber-700" : "text-slate-500"}`}>Unregistered UPIs</div>
                    <div className={`mt-1 font-display text-2xl font-bold ${unregisteredCount > 0 ? "text-amber-800" : "text-slate-800"}`}>{unregisteredCount}</div>
                    <div className={`text-[11px] ${unregisteredCount > 0 ? "text-amber-700/70" : "text-slate-500"}`}>Received money but not in whitelist</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Unknown / missing</div>
                    <div className="mt-1 font-display text-2xl font-bold text-slate-900">{unknownRow ? unknownRow.count : 0}</div>
                    <div className="text-[11px] text-slate-500">{unknownRow ? inr0(unknownRow.amount) : "no missing UPI"}</div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input placeholder="Filter by UPI, label or party…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="upi-report-search" />
                </div>
                <div className="text-[11px] text-slate-500">Showing {filtered.length} of {rows.length}</div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="display" label="Receiver UPI" /></th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-right"><SortHeader sort={sort} onSort={setSort} by="count" label="Slips" /></th>
                                <th className="px-4 py-3 text-right"><SortHeader sort={sort} onSort={setSort} by="amount" label="Amount" /></th>
                                <th className="px-4 py-3 text-right"><SortHeader sort={sort} onSort={setSort} by="party_count" label="Parties" /></th>
                                <th className="px-4 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="earliest" label="First received" /></th>
                                <th className="px-4 py-3 text-left"><SortHeader sort={sort} onSort={setSort} by="latest" label="Last received" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && rows.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-400">Loading…</td></tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-14 text-center text-slate-400">
                                    <Bank size={28} className="mx-auto mb-2" /> No verified payments in this window.
                                </td></tr>
                            )}
                            {filtered.map((r) => {
                                const isUnknown = !r.upi_id;
                                const key = r.is_master ? `M:${r.mapping_id}` : (r.upi_id || "__unknown__");
                                const isOpen = expanded === key;
                                const slipRows = detailsCache[key] || [];
                                const breakdown = r.linked_breakdown || [];
                                return (
                                    <React.Fragment key={key}>
                                    <tr
                                        data-testid={`upi-row-${r.is_master ? `master-${r.mapping_id}` : (r.upi_id || "unknown")}`}
                                        onClick={() => toggleExpand(r)}
                                        className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${!r.in_whitelist && !isUnknown ? "bg-amber-50/30" : ""} ${r.is_master ? "bg-indigo-50/40" : ""} ${isOpen ? "bg-blue-50/50" : ""}`}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-start gap-1.5">
                                                <span className="mt-0.5 text-slate-400" data-testid={`upi-expand-${r.upi_id || "unknown"}`}>
                                                    {isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className={`font-mono text-xs font-semibold ${isUnknown ? "italic text-slate-400" : "text-slate-900"}`}>{r.display}</div>
                                                        {r.is_master && (
                                                            <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-indigo-700">
                                                                Master · {(r.linked_upi_ids || []).length}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {r.is_master && (r.linked_upi_ids || []).length > 0 && (
                                                        <div className="mt-0.5 truncate text-[10px] text-indigo-600" title={(r.linked_upi_ids || []).join(", ")}>
                                                            {(r.linked_upi_ids || []).slice(0, 3).join(", ")}{(r.linked_upi_ids || []).length > 3 ? ` +${(r.linked_upi_ids || []).length - 3}` : ""}
                                                        </div>
                                                    )}
                                                    {!r.is_master && r.whitelist_label && <div className="mt-0.5 text-[10px] font-semibold text-emerald-700">Belongs to: {r.whitelist_label}</div>}
                                                    {(r.party_names_sample || []).length > 0 && (
                                                        <div className="mt-0.5 truncate text-[10px] text-slate-400" title={r.party_names_sample.join(", ")}>from: {r.party_names_sample.slice(0, 3).join(", ")}{r.party_count > 3 ? ` +${r.party_count - 3} more` : ""}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {isUnknown ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Missing UPI</span>
                                            ) : r.is_master ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                                    <CheckCircle size={10} weight="fill" /> Master UPI
                                                </span>
                                            ) : r.in_whitelist ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                    <CheckCircle size={10} weight="fill" /> Whitelisted
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                                    <Warning size={10} weight="fill" /> Unregistered
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono">{r.count}</td>
                                        <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{inr(r.amount)}</td>
                                        <td className="px-4 py-3 text-right font-mono">{r.party_count}</td>
                                        <td className="px-4 py-3 text-slate-600">{r.earliest || "—"}</td>
                                        <td className="px-4 py-3 text-slate-600">{r.latest || "—"}</td>
                                    </tr>
                                    {isOpen && (
                                        <tr data-testid={`upi-details-${r.upi_id || "unknown"}`} className="bg-slate-50/70">
                                            <td colSpan={7} className="px-6 py-3">
                                                {r.is_master && breakdown.length > 0 && (
                                                    <div className="mb-3 rounded-md border border-indigo-200 bg-indigo-50/60 p-3">
                                                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                                            <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Breakdown by linked UPI</div>
                                                            {/* v1.25.73 — Download this drill-down (all slips for this Master UPI) as PDF or Excel. */}
                                                            <DrillDownDownload
                                                                params={{
                                                                    master_upi_id: r.mapping_id,
                                                                    date_from: dateFrom || undefined,
                                                                    date_to: dateTo || undefined,
                                                                    party_id: partyId || undefined,
                                                                }}
                                                                testIdBase={`drill-master-${r.mapping_id}`}
                                                            />
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {breakdown.map((b) => (
                                                                <div key={b.upi_id} className="rounded-md bg-white px-2.5 py-1.5 text-[11px] shadow-sm ring-1 ring-indigo-100">
                                                                    <span className="font-mono font-semibold text-slate-800">{b.upi_id}</span>
                                                                    <span className="ml-2 text-slate-500">{b.count} slip{b.count === 1 ? "" : "s"}</span>
                                                                    <span className="ml-2 font-mono font-bold text-emerald-700">{inr(b.amount)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* v1.25.73 — Single-UPI drill-down also gets its own download row */}
                                                {!r.is_master && slipRows.length > 0 && (
                                                    <div className="mb-2 flex justify-end">
                                                        <DrillDownDownload
                                                            params={{
                                                                upi_id: r.upi_id || "",
                                                                date_from: dateFrom || undefined,
                                                                date_to: dateTo || undefined,
                                                                party_id: partyId || undefined,
                                                            }}
                                                            testIdBase={`drill-upi-${r.upi_id || "unknown"}`}
                                                        />
                                                    </div>
                                                )}
                                                {detailsLoadingKey === key ? (
                                                    <div className="py-4 text-center text-xs text-slate-400">Loading payments…</div>
                                                ) : slipRows.length === 0 ? (
                                                    <div className="py-4 text-center text-xs text-slate-400">No individual slips found.</div>
                                                ) : (
                                                    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
                                                        <table className="w-full text-xs">
                                                            <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                                                                <tr>
                                                                    <th className="px-3 py-2 text-left">Payment Date</th>
                                                                    <th className="px-3 py-2 text-left">Party</th>
                                                                    <th className="px-3 py-2 text-left">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setSlipSort((s) => s?.by === "utr" && s.dir === "desc" ? { by: "utr", dir: "asc" } : (s?.by === "utr" && s.dir === "asc" ? { by: null, dir: null } : { by: "utr", dir: "desc" }))}
                                                                            className="inline-flex items-center gap-1 hover:underline underline-offset-4"
                                                                            data-testid="upi-drill-sort-utr"
                                                                            title="Sort UTR A → Z / Z → A"
                                                                        >UTR {slipSort?.by === "utr" ? (slipSort.dir === "asc" ? "↑" : "↓") : "↕"}</button>
                                                                    </th>
                                                                    {r.is_master && <th className="px-3 py-2 text-left">Received on</th>}
                                                                    <th className="px-3 py-2 text-left">Payment App</th>
                                                                    <th className="px-3 py-2 text-right">Amount</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {sortRows(slipRows, slipSort).map((s) => (
                                                                    <tr key={s.id} data-testid={`upi-slip-${s.id}`} onClick={() => openSlip(s)} className="cursor-pointer border-t border-slate-100 hover:bg-blue-50">
                                                                        <td className="px-3 py-2 text-slate-700">{s.payment_date || "—"}</td>
                                                                        <td className="px-3 py-2 font-medium text-slate-900">{s.party_name}</td>
                                                                        <td className="px-3 py-2 font-mono text-slate-600">{s.utr || "—"}</td>
                                                                        {r.is_master && <td className="px-3 py-2 font-mono text-[10px] text-indigo-700">{s.receiver_upi_id || "—"}</td>}
                                                                        <td className="px-3 py-2 text-slate-600">{s.payment_app || "—"}</td>
                                                                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">{inr(s.amount)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 text-[10px] font-semibold text-slate-600">
                                                            {slipRows.length} slip{slipRows.length === 1 ? "" : "s"} · Click a row to open the slip
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {unused.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Whitelisted UPIs with zero payments in this window</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {unused.map((u) => (
                            <div key={u.upi_id} className="rounded-full bg-white px-3 py-1 text-[11px] font-mono text-slate-600 shadow-sm ring-1 ring-slate-200">
                                {u.display}{u.whitelist_label ? ` · ${u.whitelist_label}` : ""}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
